import React from 'react';
import Carousel from 'react-multi-carousel';
import { Box, Image, Skeleton, Link, IconButton } from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import CustomDot from '../CustomDot';
import { useSliderBannerPublicContext } from '../../../controller/public/slider_banner';

const responsive = {
  desktop: {
    breakpoint: {
      max: 3000,
      min: 1024,
    },
    items: 1,
  },
  mobile: {
    breakpoint: {
      max: 464,
      min: 0,
    },
    items: 1,
  },
  tablet: {
    breakpoint: {
      max: 1024,
      min: 464,
    },
    items: 1,
  },
};

interface CustomArrowProps {
  onClick?: () => void;
  direction: 'left' | 'right';
}

const CustomArrow: React.FC<CustomArrowProps> = ({ onClick, direction }) => {
  return (
    <IconButton
      aria-label={direction === 'left' ? 'Previous slide' : 'Next slide'}
      icon={direction === 'left' ? <FaChevronLeft /> : <FaChevronRight />}
      onClick={onClick}
      position="absolute"
      top="50%"
      transform="translateY(-50%)"
      left={direction === 'left' ? '20px' : 'auto'}
      right={direction === 'right' ? '20px' : 'auto'}
      zIndex={2}
      bg="rgba(255, 255, 255, 0.3)"
      color="white"
      borderRadius="full"
      border="1px solid white"
      width="72px"
      height="72px"
      _hover={{
        bg: 'rgba(255, 255, 255, 0.5)',
      }}
      fontSize="16px"
    />
  );
};

/**
 * Renders a Hero Carousel component for desktop.
 *
 * @returns {React.FC} The HeroCarousel component.
 * @author Bagas Prasetyadi
 */

const HeroCarouselDekstop: React.FC = () => {
  const { sliderBannerData: data, loadingData } =
    useSliderBannerPublicContext().state;

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" my={8}>
        <Skeleton height="350px" width="80%" />
      </Box>
    );
  }

  if (data.length === 1) {
    return (
      <Box
        w="full"
        bg="green.600"
        display="flex"
        justifyContent="center"
        py={8}
      >
        <Image src={data[0].file} h="50vh" />
      </Box>
    );
  }

  return (
    <Box
      w="full"
      bg="green.600"
      display="flex"
      justifyContent="center"
      position="relative"
    >
      <Carousel
        customLeftArrow={<CustomArrow direction="left" />}
        customRightArrow={<CustomArrow direction="right" />}
        autoPlay
        autoPlaySpeed={5000}
        className="hero-carousel-fullwidth"
        containerClass="container-with-dots"
        draggable
        focusOnSelect={false}
        infinite
        responsive={responsive}
        showDots
        slidesToSlide={1}
        swipeable
        customDot={<CustomDot />}
        dotListClass="dot-carousel"
      >
        {data.map((item) =>
          item.link ? (
            <Link
              href={item.link}
              isExternal
              key={item.id}
              display="flex"
              position="relative"
            >
              <Image
                fallback={<Skeleton h="full" w="full" />}
                src={item.file}
                alt={`Hero ${item.title}`}
                w="full"
                h="546px"
                objectFit="cover"
              />
            </Link>
          ) : (
            <Box key={item.id} display="flex" position="relative">
              <Image
                fallback={<Skeleton h="full" w="full" />}
                src={item.file}
                alt={`Hero ${item.title}`}
                w="full"
                h="546px"
                objectFit="cover"
              />
            </Box>
          )
        )}
      </Carousel>
    </Box>
  );
};

export default HeroCarouselDekstop;
